/**
 * Design tokens extracted from `blueprint codebase/BoB Manager.dc.html`.
 *
 * Constitution Principle I (NON-NEGOTIABLE): the blueprint is the only design authority.
 * Every value here was read out of the blueprint's own markup — nothing was re-chosen, and
 * nothing was invented for surfaces the blueprint does not show. The Login and Settings
 * pages compose from these same tokens rather than introducing a second visual language.
 *
 * When implementation and blueprint diverge, this file is corrected — never the blueprint.
 */

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

/**
 * Every value is a reference into `theme/global.css`, which holds the light palette (the
 * blueprint's, with its citations) and the dark one (2026-08-29). The hex fallback is what renders
 * if the stylesheet has not loaded, and it is always the LIGHT value — a half-painted light page
 * is recoverable, a half-painted dark one looks broken.
 *
 * The import surface is unchanged: components still write `color.ink`, and now get a colour that
 * answers to `prefers-color-scheme`. The reason values could not stay here is that this palette is
 * consumed almost entirely by inline `style={{}}` objects, and an inline style cannot answer a
 * media query.
 *
 * **One rule.** These strings are `var(...)` expressions, so they cannot be taken apart: a
 * template literal like `${color.nursing}14` for an 8% wash produces `var(--c-nursing)14`, which
 * is not a colour and fails silently to transparent. Use `tint()` below. No token was being
 * concatenated when this changed (only API-served series colours and `regionAccent`, both checked)
 * — this note exists so it stays that way.
 */
export const color = {
  appBg: 'var(--c-app-bg, #eef1f5)',
  ink: 'var(--c-ink, #0d1526)',
  inkMuted: 'var(--c-ink-muted, #5b6b86)',
  inkSubtle: 'var(--c-ink-subtle, #8794a4)',
  inkFaint: 'var(--c-ink-faint, #98a3b3)',
  inkBody: 'var(--c-ink-body, #475467)',
  inkNumeric: 'var(--c-ink-numeric, #26384f)',
  inkRow: 'var(--c-ink-row, #344256)',
  inkHint: 'var(--c-ink-hint, #a7b1be)',

  surface: 'var(--c-surface, #ffffff)',
  surfaceSubtle: 'var(--c-surface-subtle, #f8fafc)',
  surfaceTrack: 'var(--c-surface-track, #e4e9ef)',
  surfaceSorted: 'var(--c-surface-sorted, #e8edf3)',
  surfaceSelected: 'var(--c-surface-selected, #fff9d6)',
  surfaceChip: 'var(--c-surface-chip, #f1f4f8)',

  border: 'var(--c-border, #e1e6ec)',
  borderStrong: 'var(--c-border-strong, #dce2ea)',
  borderSubtle: 'var(--c-border-subtle, #eef1f5)',
  borderChip: 'var(--c-border-chip, #e3e8ef)',

  /**
   * The featured card's edge — the same grey line every card is drawn with, a few shades heavier.
   * See `featuredPanel` below, and the note in `global.css` for why it is NOT `borderStrong`.
   */
  panelRing: 'var(--c-panel-ring, #a7b1be)',

  sidebarBg: 'var(--c-sidebar-bg, #0e1b2c)',
  sidebarInk: 'var(--c-sidebar-ink, #c4d0de)',
  sidebarInkStrong: 'var(--c-sidebar-ink-strong, #e6ecf3)',
  sidebarInkMuted: 'var(--c-sidebar-ink-muted, #7f8ea3)',
  sidebarSectionLabel: 'var(--c-sidebar-section-label, #5f7086)',
  sidebarAvatarBg: 'var(--c-sidebar-avatar-bg, #26384f)',
  sidebarHover: 'var(--c-sidebar-hover, rgba(255,255,255,.06))',
  sidebarDivider: 'var(--c-sidebar-divider, rgba(255,255,255,.08))',

  brandFrom: 'var(--c-brand-from, #14b8a6)',
  brandTo: 'var(--c-brand-to, #0f766e)',

  positive: 'var(--c-positive, #059669)',
  negative: 'var(--c-negative, #c0392b)',
  negativeStrong: 'var(--c-negative-strong, #dc2626)',
  alert: 'var(--c-alert, #e5484d)',
  warning: 'var(--c-warning, #b45309)',
  warningBg: 'var(--c-warning-bg, #fff7e6)',
  warningBorder: 'var(--c-warning-border, #f0d489)',
  nursing: 'var(--c-nursing, #0f766e)',
  allied: 'var(--c-allied, #4338ca)',
  target: 'var(--c-target, #12b76a)',

  rowHover: 'var(--c-row-hover, #f6f8fb)',
  headerHover: 'var(--c-header-hover, #eef2f7)',

  tipBg: 'var(--c-tip-bg, #fff4cc)',
  tipBorder: 'var(--c-tip-border, rgba(240,216,120,.7))',
  tipBorderInner: 'var(--c-tip-border-inner, rgba(236,216,143,.85))',
  tipInk: 'var(--c-tip-ink, #5c4a08)',
  tipInkRow: 'var(--c-tip-ink-row, #6b5810)',

  /** The header's frosted glass. */
  headerBg: 'var(--c-header-bg, rgba(238,241,245,.86))',
  /**
   * Text on a saturated accent — a region badge, an avatar, a leader pill.
   *
   * Replaces a literal `'#fff'` at ten sites (2026-08-29). It is a token because it is NOT white
   * in dark mode: the accents are lightened there so they can also serve as a foreground colour,
   * and white text on `#2dd4bf` is 1.7:1. Anything drawn on `regionAccent`/`personAccent` must use
   * this; anything drawn on the brand gradient stays `'#fff'`, because that gradient does not
   * change with the theme.
   */
  onAccent: 'var(--c-on-accent, #ffffff)',
  dangerBg: 'var(--c-danger-bg, #fdecec)',
  dangerBorder: 'var(--c-danger-border, #f5c9c9)',
  /** The Alerts count badge (nav + bell), AL-35. */
  badgeAlert: 'var(--c-badge-alert, #c0392b)',
  onBadgeAlert: 'var(--c-on-badge-alert, #ffffff)',
  dangerInk: 'var(--c-danger-ink, #7f1d1d)',
  noticeBg: 'var(--c-notice-bg, #fef2df)',
  noticeInk: 'var(--c-notice-ink, #7a4e08)',
  scrim: 'var(--c-scrim, rgba(13,21,38,.42))',

  /**
   * Text on the BRAND gradient, which is the same in both themes — so unlike `onAccent`, this
   * stays white. The sign-in button and the sidebar tile; nothing else.
   */
  onBrand: 'var(--c-on-brand, #ffffff)',

  /** Badge washes. Two pale greens because the blueprint specifies two — see `global.css`. */
  washTeal: 'var(--c-wash-teal, #ccfbf1)',
  washMint: 'var(--c-wash-mint, #e7f7ef)',
  washIndigo: 'var(--c-wash-indigo, #e0e7ff)',

  /** The Audit page (spec 007) — cadence pills, the flagged-row tint, the neutral trend mark. */
  cadenceDailyBg: 'var(--c-cadence-daily-bg, #ccfbf1)',
  cadenceDailyInk: 'var(--c-cadence-daily-ink, #0f766e)',
  cadenceWeeklyBg: 'var(--c-cadence-weekly-bg, #e0e7ff)',
  cadenceWeeklyInk: 'var(--c-cadence-weekly-ink, #4338ca)',
  cadenceMonthlyBg: 'var(--c-cadence-monthly-bg, #fef3c7)',
  cadenceMonthlyInk: 'var(--c-cadence-monthly-ink, #b45309)',
  flaggedRow: 'var(--c-flagged-row, #fef7f7)',
  trendNeutral: 'var(--c-trend-neutral, #98a3b3)',
  inkMintStrong: 'var(--c-ink-mint-strong, #0f6b47)',
  borderMint: 'var(--c-border-mint, #bfe6d4)',

  /** Data-column accents that are not series colours. */
  colTarget: 'var(--c-col-target, #4a86e8)',
  colProspect: 'var(--c-col-prospect, #6d28d9)',
  colProspectAlt: 'var(--c-col-prospect-alt, #7c3aed)',

  sidebarInkFaint: 'var(--c-sidebar-ink-faint, #aab6c6)',
  sidebarInkBody: 'var(--c-sidebar-ink-body, #cdd9e6)',
  sidebarInkActive: 'var(--c-sidebar-ink-active, #ffffff)',
  sidebarActive: 'var(--c-sidebar-active, rgba(255,255,255,.1))',

  /** The Cards view's five velocity tiles (PRD §9.8.5). */
  tileBobBg: 'var(--c-tile-bob-bg, #eef2fb)',
  tileBobInk: 'var(--c-tile-bob-ink, #374151)',
  tileToaBg: 'var(--c-tile-toa-bg, #f2f6ff)',
  tilePendingBg: 'var(--c-tile-pending-bg, #fff7ec)',
  tilePendingLabel: 'var(--c-tile-pending-label, #86735a)',
  tileProspectBg: 'var(--c-tile-prospect-bg, #f6f1fe)',
  tileProspectLabel: 'var(--c-tile-prospect-label, #6f6484)',
  tilePempBg: 'var(--c-tile-pemp-bg, #f4f5f7)',
  tilePempLabel: 'var(--c-tile-pemp-label, #767f8c)',

  /** Sign-in button chrome. Brand, so it does not follow the theme. */
  signInFrom: 'var(--c-signin-from, #4338ca)',
  signInGlow: 'var(--c-signin-glow, 0 4px 14px -6px rgba(67,56,202,.5))',
} as const;

/**
 * An n% wash of a colour, for the "this chip is selected" fills.
 *
 * Replaces `${accent}14` — appending two hex digits for alpha, which cannot work once the colour
 * is a `var()`. `color-mix` is the CSS-native equivalent and has the advantage of also working on
 * the `rgba()` and `var()` values in this file, where hex-appending never did.
 */
export function tint(value: string, percent: number): string {
  return `color-mix(in srgb, ${value} ${percent}%, transparent)`;
}

/**
 * Chart series colours. Served by the API so PRD §6.2 and the blueprint cannot drift apart —
 * this mirror exists for the surfaces that borrow a series hue without fetching a chart.
 *
 * `offers` is **magenta, not the blueprint's `#7c3aed` violet** (owner decision, 2026-08-28).
 * Violet was 12.4 ΔE2000 from `toa` where the other three series are 49–52 apart, so the two
 * lines read as one; `#d20096` is 30.6 — the most any fourth colour can manage without clashing
 * with the SPOT pill. The full measurement lives in `backend/src/domain/series.ts`, which is the
 * source of truth: change it there first, or the chart and everything keyed off this map
 * disagree.
 */
export const seriesColor = {
  toa: 'var(--c-series-toa, #1d4ed8)',
  jobs: 'var(--c-series-jobs, #b45309)',
  subs: 'var(--c-series-subs, #059669)',
  offers: 'var(--c-series-offers, #d20096)',
} as const;

/** Region accent colours — `accentFor()` in the blueprint. */
export const regionAccent: Record<string, string> = {
  R1: 'var(--c-accent-r1, #0f766e)',
  R2: 'var(--c-accent-r2, #1d4ed8)',
  R3: 'var(--c-accent-r3, #6d28d9)',
  R4: 'var(--c-accent-r4, #b45309)',
  R5: 'var(--c-accent-r5, #0e7490)',
  SPOT: 'var(--c-accent-spot, #be123c)',
};

export const DEFAULT_REGION_ACCENT = 'var(--c-accent-default, #0f766e)';

export function accentFor(regionCode: string): string {
  return regionAccent[regionCode] ?? DEFAULT_REGION_ACCENT;
}

// ---------------------------------------------------------------------------
// Typography — IBM Plex Sans (UI) / IBM Plex Mono (numerics). Principle I.
// ---------------------------------------------------------------------------

export const font = {
  sans: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
} as const;

/** Sizes are the literal values used in the blueprint, half-pixels included. */
export const fontSize = {
  /** Column headers in the dense region table */
  tableHeader: '9px',
  /** Column headers in the wider breakdown tables */
  tableHeaderWide: '10px',
  micro: '10px',
  label: '10.5px',
  caption: '11px',
  captionStrong: '11.5px',
  body: '12px',
  bodyStrong: '12.5px',
  control: '13px',
  navItem: '13.5px',
  sectionTitle: '14px',
  brand: '15px',
  cardTitle: '17px',
  pageTitle: '20px',
  detailTitle: '22px',
  kpiValue: '25px',
  statValue: '19px',
  bobValue: '34px',
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const letterSpacing = {
  tightest: '-.4px',
  tighter: '-.3px',
  tight: '-.2px',
  label: '.3px',
  labelWide: '.4px',
  wide: '.5px',
  widest: '.9px',
} as const;

// ---------------------------------------------------------------------------
// Space, radius, elevation
// ---------------------------------------------------------------------------

export const space = {
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '10px',
  '2xl': '12px',
  '3xl': '14px',
  '4xl': '16px',
  '5xl': '18px',
  '6xl': '20px',
  '7xl': '22px',
  '8xl': '30px',
} as const;

export const radius = {
  xs: '2px',
  sm: '5px',
  md: '6px',
  lg: '7px',
  xl: '8px',
  '2xl': '9px',
  '3xl': '11px',
  '4xl': '13px',
  '5xl': '14px',
  '6xl': '15px',
  pill: '20px',
  circle: '50%',
} as const;

export const elevation = {
  card: 'var(--c-shadow-card, 0 10px 34px -10px rgba(16,35,59,.28))',
  /** The featured card's halo — the 3px wash outside `panelRing`, plus a shallow lift. */
  panel:
    'var(--c-shadow-panel, 0 0 0 3px rgba(16,35,59,.05), 0 10px 26px -18px rgba(16,35,59,.45))',
  palette: 'var(--c-shadow-palette, 0 18px 44px -14px rgba(16,35,59,.34))',
  search: 'var(--c-shadow-search, 0 4px 16px -8px rgba(16,35,59,.22))',
  /** The brand tile's own glow — a property of the mark, so it does not follow the theme. */
  brand: '0 4px 12px -2px rgba(20,184,166,.5)',
} as const;

/**
 * The chrome of a **featured** card — the line-chart card on all three pages that carry one, and
 * the Region Performance card in each of its three states (owner request 2026-08-30).
 *
 * One object rather than three copies of the same three properties, because the whole value of
 * the treatment is that those cards match: a ring applied to the chart and missed on the table
 * below it reads as a rendering fault, and card chrome is duplicated inline across four files
 * here, so "missed one" is the likely failure.
 *
 * It carries **no `borderRadius`** deliberately. The panels are `radius['6xl']` and `<DataTable>`
 * is `radius['5xl']`, both from the blueprint, and folding a radius in here would silently
 * restyle one of them.
 */
export const featuredPanel = {
  background: color.surface,
  border: `1px solid ${color.panelRing}`,
  boxShadow: elevation.panel,
} as const;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export const layout = {
  sidebarExpanded: '236px',
  /**
   * The collapsed rail keeps the page name under the icon (owner decision, 2026-08-28), so it is
   * wider than the blueprint's icon-only rail. `Leaderboard` at `fontSize.label` is the widest
   * label; drop below this and it wraps mid-word, which is why the number is not a round 80.
   */
  sidebarCollapsed: '96px',
  /** Desktop-first: the blueprint's design width. Narrower degrades, never redesigns (FR-057). */
  contentMinWidth: '1180px',
  /**
   * The By Client breakdown's own floor — sixteen metric columns plus a 68px action column
   * (FR-091, 2026-09-12; was 1260px before it, and 1300px while that column was 40px wide).
   *
   * ⚠ **Widened rather than absorbed.** The table's first two tracks are `1.5fr`/`1.3fr` and the
   * rest are `1fr`, so a fixed column added without moving this takes its width off the sixteen
   * metric tracks — which is how `#JOBS` came to be cut on Region Performance. This has to track
   * `ACTION_COLUMN_WIDTH` in `ClientBreakdown.tsx` exactly: the column grew to 68px when it
   * gained its `Action` header (2026-09-14), and leaving this at 1300px would have paid for the
   * word out of the metric columns instead. This table already scrolls sideways below its floor,
   * so the move changes nothing about when it does.
   */
  breakdownMinWidth: '1328px',
  headerPadding: '16px 30px',
  contentPadding: '22px 30px 60px',
} as const;

/**
 * The six UI regions are a fixed navigation set, never a query result (PRD §6.2).
 *
 * ⚠ **`SPA` is not one of them, and the API still serves it** (owner decision 2026-09-02).
 * The warehouse reaches SPA by no route at all — `client_alignment` carries no SPA rows, so
 * every SPA figure in every snapshot is `0`/`—` — and a seventh chip that can only ever read
 * empty is a region a reader keeps re-checking. Removing it is a **presentation** decision: the
 * backend's own `UI_REGIONS` still has seven entries, the read model still stores a SPA row, and
 * nothing here asks it to stop.
 *
 * That is why this list cannot be the whole of the change. Pills, table rows, region chips and
 * account results are all *served* collections, so they arrive with SPA in them whatever this
 * array says; `api/regionScope.ts` drops it from every one of them at the fetch boundary, and
 * `isUiRegion` below is the single predicate both sides use. Restore `SPA` here and the scrubber
 * stops removing it — which is the intended way back if SPA ever carries data.
 */
export const UI_REGIONS = ['R1', 'R2', 'R3', 'R4', 'R5', 'SPOT'] as const;
export type UiRegion = (typeof UI_REGIONS)[number];

export const REGION_NAMES: Record<UiRegion, string> = {
  R1: 'Northeast',
  R2: 'Southeast',
  R3: 'Midwest',
  R4: 'South Central',
  R5: 'West',
  SPOT: 'SPOT',
};

/** SPOT is Special; R1–R5 are Regions. A property of the code, not the data (§9.7.1). */
export const SPECIAL_REGIONS: readonly UiRegion[] = ['SPOT'];

/**
 * Is this code one of the regions this client renders?
 *
 * The one membership test in the frontend, so the scrubber, the region route and the Delivery
 * badges cannot disagree about what the set is. A served code that fails it is not an error —
 * today it is SPA, and it is dropped rather than surfaced.
 */
export function isUiRegion(code: string | null | undefined): code is UiRegion {
  return code != null && (UI_REGIONS as readonly string[]).includes(code);
}

/**
 * A stable accent for a person (spec 002).
 *
 * Leaders, managers and recruiters have no colour in the source, and the blueprint gives every
 * avatar one. Derived from the identity key rather than from a list position, so a person keeps
 * their colour when the list is re-sorted or filtered — a colour that moved with the sort would
 * read as a change in the data.
 *
 * The palette is the blueprint's own region accents, reused deliberately: a fixed set of hues
 * across the product beats a second palette that drifts from the first.
 */
export const PERSON_ACCENTS: readonly string[] = [
  '#0f766e',
  '#1d4ed8',
  '#6d28d9',
  '#b45309',
  '#0e7490',
  '#be123c',
  '#7c3aed',
];

export function personAccent(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) % 100_000;
  return PERSON_ACCENTS[hash % PERSON_ACCENTS.length]!;
}

/** Two-letter initials for an avatar. Falls back to `··` rather than rendering an empty box. */
export function initialsOf(name: string | null | undefined): string {
  return (
    (name ?? '')
      .split(/[\s.@_-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '··'
  );
}
